import React, { useId, useRef, useState } from "react";
import { event } from "../../utils/event";

// Auto-grow limits, matched to the feed's comment box so both fields behave
// identically: grow to just under 5 lines, then scroll inside the field.
const LINE_HEIGHT = 24;
const MAX_HEIGHT = 112; // same cap as FeedPage's comment box (~4.7 lines)

// Grow the field to fit its content, capping at MAX_HEIGHT and only then
// switching on its own scrollbar. Height must be reset to "auto" first or
// scrollHeight keeps reporting the previous (larger) box and it never shrinks.
const autoGrow = (el) => {
  if (!el) return;
  el.style.height = "auto";
  if (el.scrollHeight > MAX_HEIGHT) {
    el.style.height = `${MAX_HEIGHT}px`;
    el.style.overflowY = "auto";
  } else {
    el.style.height = `${el.scrollHeight}px`;
    el.style.overflowY = "hidden";
  }
};

const PAPERCLIP_PATH =
  "M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13";

const Paperclip = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    className="h-5 w-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d={PAPERCLIP_PATH}
    />
  </svg>
);

const CLIP_CLASSES =
  "flex h-9 w-9 flex-shrink-0 cursor-pointer items-center justify-center rounded-full text-secondary hover:bg-[#F5F7F7]";

// Message composer.
//
// Spec: paperclip only (no camera glyph), placeholder "Message" with NO
// ellipsis and in tertiary grey — the only real placeholder on either Chats
// screen. Send is #004C50 when there is text and greyed + disabled until then.
//
// The field is a textarea, not an input: a long message has to wrap so it can be
// proof-read before sending, rather than scrolling out of sight on one line.
// Same behaviour as the feed's comment box — grow to 4 lines, then scroll, with
// Enter to send and Shift+Enter for a deliberate newline.
export default function Composer({ onSend, onAttach, disabled = false }) {
  const [text, setText] = useState("");
  const fileId = useId();
  const fieldRef = useRef(null);
  const hasText = text.trim().length > 0;
  const canSend = hasText && !disabled;
  // Inside the Flutter in-app webview an <input type="file"> does nothing; the
  // wrapper picks + uploads the file natively and fires "myCustomChatEvent" with
  // the resulting path, which useChatThread listens for.
  const isFlutterWebView = !!localStorage.getItem("isFlutterInAppWebViewReady");

  const submit = (e) => {
    e?.preventDefault?.();
    if (!canSend) return;
    onSend(text);
    setText("");
    // Collapse back to a single row — clearing the value alone leaves the
    // inline height from the last keystroke.
    if (fieldRef.current) {
      fieldRef.current.style.height = "auto";
      fieldRef.current.style.overflowY = "hidden";
    }
  };

  return (
    // Desktop (≥1024): the footer is a padded band and the controls sit inside a
    // rounded, bordered field — 24px clear of the panel's left/right edges, ~54px
    // tall at rest, 20px radius (restrained, not a pill), full panel width.
    // Mobile keeps the flat open footer it already shipped with, so every
    // rounded-field rule is behind lg:.
    <form
      onSubmit={submit}
      className="shrink-0 border-t border-divider bg-white px-3 py-2 lg:px-6 lg:py-4"
    >
      {/* items-end on mobile keeps the icons on the last line's baseline;
          lg:items-center centres them in the rounded field as spec'd. */}
      <div className="flex items-end gap-2 lg:min-h-[54px] lg:items-center lg:gap-3 lg:rounded-[20px] lg:border lg:border-gray-200 lg:px-4">
        {isFlutterWebView ? (
          <button
            type="button"
            aria-label="Attach a file"
            onClick={() => event("imageUploadChat")}
            className={CLIP_CLASSES}
          >
            <Paperclip />
          </button>
        ) : (
          <>
            <input
              type="file"
              id={fileId}
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onAttach(file);
                e.target.value = "";
              }}
            />
            <label htmlFor={fileId} aria-label="Attach a file" className={CLIP_CLASSES}>
              <Paperclip />
            </label>
          </>
        )}

        <textarea
          ref={fieldRef}
          rows={1}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            autoGrow(e.target);
          }}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter inserts a newline. Without this a textarea
            // would only ever add lines and the send button would be the only way
            // to post — a regression against the old single-line input.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Message"
          aria-label="Message"
          enterKeyHint="send"
          autoComplete="off"
          className="min-w-0 flex-1 resize-none bg-transparent py-2 text-sm text-gray-900 placeholder-tertiary focus:outline-none"
          style={{
            maxHeight: `${MAX_HEIGHT}px`,
            lineHeight: `${LINE_HEIGHT}px`,
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-y",
          }}
        />

        {/* Icon-only send. No spinner anywhere in the product, so on an icon
            button the dim IS the in-flight signal — `canSend` is false while
            the message is sending. aria-busy carries it for assistive tech. */}
        <button
          type="submit"
          disabled={!canSend}
          aria-busy={disabled ? "true" : undefined}
          aria-label="Send message"
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
            canSend
              ? "text-primaryColor hover:bg-[#F5F7F7]"
              : "cursor-default text-tertiary"
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-5 w-5 rotate-90"
          >
            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
          </svg>
        </button>
      </div>
    </form>
  );
}
